import type { CapabilityState, MobileMode } from '../api';
import type { ProjSection, View } from '../App';

/*
 * Навигация телефона выводится из раскладки, которая приходит в профиле. Здесь
 * только чистые функции: раскладка на входе, состав меню на выходе. Про реестр
 * клиент не знает, тот живёт на сервере (server/src/access/registry.ts).
 *
 * Раскладка называет раздел кодом записи верхнего уровня, а не коротким именем
 * вкладки, поэтому здесь же лежит перевод одного в другое.
 */

/** Порядок разделов в полосе — тот же, что в боковом меню (Shell.tsx). */
const ORDER: View[] = ['projects', 'calendar', 'chat', 'contracts', 'finance', 'kb'];

/** Код записи верхнего уровня → вид приложения. */
const VIEW_BY_CODE: Record<string, View> = {
  'section.projects': 'projects',
  'block.calendar': 'calendar',
  'block.chat': 'chat',
  'section.contracts': 'contracts',
  'section.kb': 'kb',
  'section.finance': 'finance',
  'block.profile': 'profile',
  'block.notifications': 'chat',
};

/** Подразделы проекта: код записи → подраздел экрана. */
const SECTION_BY_CODE: Record<string, ProjSection> = {
  'section.projects.gpr': 'gpr',
  'section.projects.packages': 'packages',
  'section.projects.sprints': 'sprints',
  'section.projects.walks': 'walks',
  'section.projects.team': 'team',
  'section.projects.settings': 'settings',
};

/** Мест в полосе; последнее всегда у профиля. */
const SLOTS = 5;

export type Tab = View | 'more';

/**
 * Раскладка пустая — ограничений нет.
 *
 * Так бывает, когда приложение новее сервера: статика уже переключилась, а
 * служба ещё не перезапустилась и профиль приходит без поля `mobile`. Пустую
 * раскладку нельзя понимать как «всё выключено»: тогда телефон показал бы одну
 * вкладку и заглушку на каждом разделе. Пока раскладки нет, приложение ведёт
 * себя как до этой работы (12.09.2026, поймано на тестовом контуре).
 */
export function limited(layout: CapabilityState[]): boolean {
  return layout.length > 0;
}

/** Поведение возможности; незнакомый код на телефон не пускает. */
export function modeOf(layout: CapabilityState[], code: string): MobileMode {
  if (!limited(layout)) return 'FULL';
  return layout.find((c) => c.code === code)?.mode ?? 'NONE';
}

/** Разделы, у которых на телефоне жива хоть одна возможность. */
export function mobileModules(layout: CapabilityState[]): string[] {
  // «Финансы» новее раскладки и на телефоне по умолчанию выключены: без неё их нет.
  if (!limited(layout)) return [...ORDER.filter((v) => v !== 'finance'), 'profile'];
  const out: string[] = [];
  for (const c of layout) {
    if (c.mode === 'NONE') continue;
    const view = VIEW_BY_CODE[c.module];
    if (view && !out.includes(view)) out.push(view);
  }
  return out;
}

/**
 * Состав нижней полосы. Пять мест, последнее у профиля. Если разделов
 * больше, чем мест, последнее свободное занимает «Ещё», а не влезшие уходят
 * в него списком (решение Ильи 11.09.2026).
 */
export function tabsOf(modules: string[]): { tabs: Tab[]; more: View[] } {
  const pool = ORDER.filter((v) => modules.includes(v));
  if (pool.length <= SLOTS - 1) return { tabs: [...pool, 'profile'], more: [] };
  return { tabs: [...pool.slice(0, SLOTS - 2), 'more', 'profile'], more: pool.slice(SLOTS - 2) };
}

/** Подразделы проекта, доступные с телефона. */
export function mobileSections(layout: CapabilityState[]): ProjSection[] {
  if (!limited(layout)) return Object.values(SECTION_BY_CODE);
  return layout
    .filter((c) => c.mode !== 'NONE' && SECTION_BY_CODE[c.code])
    .map((c) => SECTION_BY_CODE[c.code]);
}

